import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLanguage } from '../../context/LanguageContext';
import { Layers, Calculator } from 'lucide-react';

const MarkVirkeVardeKalkylator = () => {
  const { language } = useLanguage();
  const [totalPrice, setTotalPrice] = useState(5000000);
  const [timberValue, setTimberValue] = useState(3500000);
  const [result, setResult] = useState(null);

  const content = {
    sv: {
      title: 'Mark vs virkesvärde',
      description: 'Analysera fördelningen mellan markvärde och virkesvärde vid ett fastighetsköp. Viktigt för att förstå värdefördelningen.',
      totalPrice: 'Totalt fastighetspris (kr)',
      timberValue: 'Uppskattat virkesvärde (kr)',
      calculate: 'Beräkna fördelning',
      result: 'Värdefördelning',
      landValue: 'Markvärde',
      timberShare: 'Virkesvärde',
      shareOfTotal: 'Andel av totalt',
      analysis: 'Analys',
      normalRange: 'Normal fördelning: Virke 60-80%, Mark 20-40%',
      highTimber: 'Hög andel virkesvärde - virkesrikt bestånd',
      highLand: 'Hög andel markvärde - potentiellt hög bonitet eller strategiskt läge',
      balanced: 'Balanserad fördelning - typisk för produktiv skogsmark'
    },
    de: {
      title: 'Boden vs Holzwert',
      description: 'Analysieren Sie die Verteilung zwischen Bodenwert und Holzwert beim Immobilienkauf.',
      totalPrice: 'Gesamtpreis (SEK)',
      timberValue: 'Geschätzter Holzwert (SEK)',
      calculate: 'Verteilung berechnen',
      result: 'Wertverteilung',
      landValue: 'Bodenwert',
      timberShare: 'Holzwert',
      shareOfTotal: 'Anteil am Gesamtwert',
      analysis: 'Analyse',
      normalRange: 'Normale Verteilung: Holz 60-80%, Boden 20-40%',
      highTimber: 'Hoher Holzanteil - holzreiches Bestand',
      highLand: 'Hoher Bodenanteil - hohe Bonität oder strategische Lage',
      balanced: 'Ausgewogene Verteilung'
    },
    en: {
      title: 'Land vs timber value',
      description: 'Analyze the distribution between land value and timber value in a property purchase.',
      totalPrice: 'Total property price (SEK)',
      timberValue: 'Estimated timber value (SEK)',
      calculate: 'Calculate distribution',
      result: 'Value distribution',
      landValue: 'Land value',
      timberShare: 'Timber value',
      shareOfTotal: 'Share of total',
      analysis: 'Analysis',
      normalRange: 'Normal distribution: Timber 60-80%, Land 20-40%',
      highTimber: 'High timber share - timber-rich stand',
      highLand: 'High land share - potentially high site quality or strategic location',
      balanced: 'Balanced distribution'
    },
    se: {
      title: 'Eanan vs muorraárvu',
      description: 'Analyser juogadeami eatnanárvvu ja muorraárvvu gaskka opmodatoastimis.',
      totalPrice: 'Ollislaš opmodathaddi (SEK)',
      timberValue: 'Árvvoštallon muorraárvu (SEK)',
      calculate: 'Rehkenas juogadeami',
      result: 'Árvvojuogadeapmi',
      landValue: 'Eatnanárvu',
      timberShare: 'Muorraárvu',
      shareOfTotal: 'Oassi ollislašas',
      analysis: 'Analysa',
      normalRange: 'Dábálaš juogadeapmi: Muorra 60-80%, Eanan 20-40%',
      highTimber: 'Alla muorraoassi',
      highLand: 'Alla eannanoassi',
      balanced: 'Balanserejon juogadeapmi'
    }
  };

  const c = content[language] || content.sv;

  const calculateDistribution = () => {
    const landValue = totalPrice - timberValue;
    const timberPercent = (timberValue / totalPrice) * 100;
    const landPercent = (landValue / totalPrice) * 100;
    
    let analysis = c.balanced;
    if (timberPercent > 80) {
      analysis = c.highTimber;
    } else if (landPercent > 40) {
      analysis = c.highLand;
    }
    
    setResult({
      landValue,
      timberValue,
      landPercent: landPercent.toFixed(1),
      timberPercent: timberPercent.toFixed(1),
      analysis
    });
  };

  return (
    <Card className="w-full" data-testid="mark-virke-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Layers className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{c.totalPrice}</Label>
            <Input
              type="number"
              value={totalPrice}
              onChange={(e) => setTotalPrice(Number(e.target.value))}
              min={0}
              step={100000}
              data-testid="total-price-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>{c.timberValue}</Label>
            <Input
              type="number"
              value={timberValue}
              onChange={(e) => setTimberValue(Number(e.target.value))}
              min={0}
              step={100000}
              data-testid="timber-value-input"
            />
          </div>
        </div>

        <Button 
          onClick={calculateDistribution} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">{c.result}</h4>
            
            <div className="grid grid-cols-2 gap-4 pb-4">
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700">{c.landValue}</p>
                <p className="text-2xl font-bold text-amber-700">{result.landValue.toLocaleString('sv-SE')} kr</p>
                <p className="text-lg font-semibold text-amber-600">{result.landPercent}%</p>
              </div>
              <div className="text-center p-3 bg-forest-100 rounded-lg">
                <p className="text-sm text-forest-700">{c.timberShare}</p>
                <p className="text-2xl font-bold text-forest-700">{result.timberValue.toLocaleString('sv-SE')} kr</p>
                <p className="text-lg font-semibold text-forest-600">{result.timberPercent}%</p>
              </div>
            </div>
            
            {/* Visual bar */}
            <div className="mb-4">
              <div className="h-6 rounded-full overflow-hidden flex">
                <div 
                  className="bg-amber-400 transition-all"
                  style={{ width: `${result.landPercent}%` }}
                />
                <div 
                  className="bg-forest-600 transition-all"
                  style={{ width: `${result.timberPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{c.landValue} ({result.landPercent}%)</span>
                <span>{c.timberShare} ({result.timberPercent}%)</span>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-800">{c.analysis}</p>
              <p className="text-sm text-blue-700">{result.analysis}</p>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">{c.normalRange}</p>
      </CardContent>
    </Card>
  );
};

export default MarkVirkeVardeKalkylator;
